import React from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import { HTTPError } from 'ky';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { ObservationType } from '@interfaces-clean/observations.interface';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import { useTranslation } from '@hooks/index';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

export interface ObservationDeleteDialogProps {
    recordId: string;
    actionsCount: number;
    reviewLogsCount: number;
    onClose: () => void;
    studyRef: string;
    onDeleteHandler: (success: boolean, conflict?: boolean) => void;
    observationId: number;
    dataSetDate?: string | null;
    observation?: string | null;
    siteName?: string | null;
    type: ObservationType;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteObservationDialog = ({
    recordId,
    actionsCount,
    reviewLogsCount,
    onClose,
    studyRef,
    onDeleteHandler,
    observationId,
    dataSetDate,
    observation,
    siteName,
    type,
}: ObservationDeleteDialogProps) => {
    const { t } = useTranslation('cm');
    const { trigger: deleteObservation } = ObservationsApi.useDelete(studyRef);
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const onDelete = async (data: FormInputs) => {
        try {
            const response = await deleteObservation({
                deletedReason: data.deleteReason,
                observationId,
                dataSetDate,
                observation,
                siteName,
                type,
            });
            onDeleteHandler(response, false);
        } catch (err) {
            const status = (err as HTTPError)?.response?.status;
            onDeleteHandler(false, status === 409);
        }
    };

    const warningText = (
        <span className={cn('k-text-left', style.fontSize)}>
            {t('cm:observationsGrid.deleteObservationWarning1')}
            <span className={style.textHighLight}>
                {actionsCount} {t('cm:observationsGrid.deleteObservationWarning2')}
            </span>
            {t('cm:observationsGrid.deleteObservationWarning3')}
            <span className={styles.textHighLight}>
                {reviewLogsCount} {t('cm:observationsGrid.deleteObservationWarning4')}
            </span>
            {t('cm:observationsGrid.deleteObservationWarning5')}
        </span>
    );
    return (
        <DraggableDialog
            dataTestId="observation-delete--warning"
            onSubmit={() => handleSubmit(onDelete)()}
            onClose={onClose}
            title={
                type === ObservationType.Controls
                    ? `${t('cm:observationsGrid.deleteControlsObservation')}: ${recordId}`
                    : `${t('cm:deleteObservation')}: ${recordId}`
            }
            initialWidth={530}
            initialHeight={actionsCount > 0 || reviewLogsCount > 0 ? 280 : 230}
            primaryBtnLabel={t('common:delete')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {(actionsCount > 0 || reviewLogsCount > 0) && <InfoField type="Warning" message={warningText} />}
                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="deleteReason"
                    required
                    data-testid="field-select-observation"
                    label={`${t('cm:deleteReasonLabel')} *`}
                    readOnly={isSubmitting}
                />
            </div>
        </DraggableDialog>
    );
};

export default DeleteObservationDialog;
